import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Edit2, Download, Bookmark, Copy, Plus,
  ExternalLink, Trash2, LayoutTemplate, Clock, Activity,
  ChevronRight, ImagePlus,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import DesignPreview from '../components/shared/DesignPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';
const ACTIVITY_KEY = 'ivisora_activity';

/* ─── Helpers ────────────────────────────────────────────────────────────────── */
const timeAgo = (ts) => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const ACTIVITY_CFG = {
  login:      { Icon: Sparkles, bg: '#f5f3ff', color: '#7c3aed' },
  created:    { Icon: Plus,     bg: '#fef3c7', color: '#d97706' },
  edited:     { Icon: Edit2,    bg: '#dbeafe', color: '#2563eb' },
  downloaded: { Icon: Download, bg: '#d1fae5', color: '#059669' },
  saved:      { Icon: Bookmark, bg: '#ffedd5', color: '#ea580c' },
  duplicated: { Icon: Copy,     bg: '#cffafe', color: '#0891b2' },
};

/* ─── Section wrapper ────────────────────────────────────────────────────────── */
const Section = ({ title, icon: Icon, action, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-violet-500" />
        <h2 className="font-semibold text-gray-900 text-sm">{title}</h2>
      </div>
      {action && (
        <button
          onClick={action.onClick}
          className="flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors"
        >
          {action.label} <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ─── Empty state ─────────────────────────────────────────────────────────────── */
const EmptyState = ({ emoji, title, desc, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
    <div className="text-4xl">{emoji}</div>
    <div>
      <p className="font-semibold text-gray-800 text-sm">{title}</p>
      <p className="text-xs text-gray-500 mt-1 max-w-[220px] mx-auto">{desc}</p>
    </div>
    {actionLabel && (
      <button
        onClick={onAction}
        className="mt-1 text-xs font-semibold text-white px-4 py-2 rounded-lg transition-all hover:opacity-90"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)' }}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

/* ─── Skeleton card ──────────────────────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="rounded-xl border border-gray-100 overflow-hidden animate-pulse">
    <div className="h-36 bg-gray-100" />
    <div className="p-3 space-y-2">
      <div className="h-3 bg-gray-100 rounded w-3/4" />
      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
    </div>
  </div>
);

/* ─── Design card ────────────────────────────────────────────────────────────── */
const DesignCard = ({ design, onOpen, onDuplicate, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const GRAD = ['from-violet-400 to-fuchsia-400', 'from-blue-400 to-cyan-400', 'from-amber-400 to-orange-400', 'from-emerald-400 to-teal-400'];
  const grad = GRAD[design.id?.charCodeAt(0) % 4 || 0];

  return (
    <div
      className="rounded-xl border border-gray-100 overflow-hidden cursor-pointer group transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Thumbnail — same pipeline as the Templates page (full page, no crop) */}
      <DesignPreview
        design={design}
        onClick={onOpen}
        placeholder={
          <div className={`absolute inset-0 bg-gradient-to-br ${grad} flex items-center justify-center`}>
            <span className="text-white text-2xl font-black opacity-50">
              {design.name?.[0]?.toUpperCase() || 'D'}
            </span>
          </div>
        }
      >
        {hovered && (
          <div className="absolute inset-0 z-10 bg-black/30 flex items-center justify-center gap-2">
            <button
              onClick={e => { e.stopPropagation(); onOpen(); }}
              className="h-8 px-3 bg-white text-gray-900 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> Open
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDuplicate(); }}
              className="h-8 w-8 bg-white/80 rounded-lg text-gray-700 flex items-center justify-center hover:bg-white transition-colors"
              title="Duplicate"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="h-8 w-8 bg-white/80 rounded-lg text-red-500 flex items-center justify-center hover:bg-white transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </DesignPreview>
      {/* Info */}
      <div className="p-3 bg-white">
        <p className="text-sm font-semibold text-gray-900 truncate">{design.name || 'Untitled'}</p>
        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {design.updated_at ? timeAgo(design.updated_at) : 'Recently'}
        </p>
      </div>
    </div>
  );
};

/* ─── Template row ────────────────────────────────────────────────────────────── */
const TemplateRow = ({ template, onOpen, onUnsave }) => (
  <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0 group">
    <div
      className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #ede9fe, #fce7f3)' }}
    >
      {template.thumbnail ? (
        <img src={template.thumbnail} alt={template.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-violet-300 text-xl font-bold">
          {template.title?.[0] || 'T'}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 truncate">{template.title || 'Template'}</p>
      <p className="text-xs text-gray-400">{template.category || 'Design'}</p>
    </div>
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={onOpen}
        className="h-7 px-2.5 bg-violet-50 text-violet-700 rounded-lg text-xs font-semibold hover:bg-violet-100 transition-colors"
      >
        Use
      </button>
      <button
        onClick={onUnsave}
        className="h-7 w-7 text-gray-400 rounded-lg hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
        title="Remove"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

/* ─── Activity item ───────────────────────────────────────────────────────────── */
const ActivityItem = ({ item }) => {
  const cfg = ACTIVITY_CFG[item.type] || ACTIVITY_CFG.created;
  const { Icon, bg, color } = cfg;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: bg }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 leading-snug">
          <span className="font-medium">{item.title}</span>
        </p>
        <p className="text-xs text-gray-400 mt-0.5">{timeAgo(item.timestamp)}</p>
      </div>
    </div>
  );
};

/* ─── Stats card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ icon, value, label, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: color + '1a' }}>
      {icon}
    </div>
    <div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
      <div className="text-xs text-gray-500 font-medium mt-0.5">{label}</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   UserDashboard
═══════════════════════════════════════════════════════════════ */
const UserDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [recentDesigns, setRecentDesigns] = useState([]);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth guard + data load
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const userData = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
    if (!userData) { navigate('/'); return; }
    setUser(userData);

    const load = async () => {
      setLoading(true);
      if (token && API_URL) {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          const [dr, dt] = await Promise.allSettled([
            fetch(`${API_URL}/api/designs`, { headers }),
            fetch(`${API_URL}/api/user/saved-templates`, { headers }),
          ]);
          if (dr.status === 'fulfilled' && dr.value.ok) {
            const d = await dr.value.json();
            setRecentDesigns(Array.isArray(d) ? d : (d.designs || []));
          }
          if (dt.status === 'fulfilled' && dt.value.ok) {
            const d = await dt.value.json();
            setSavedTemplates(Array.isArray(d) ? d : (d.templates || []));
          }
        } catch {}
      }
      // Activity from localStorage
      const acts = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || '[]');
      setActivity(acts);
      setLoading(false);
    };
    load();
  }, [navigate]);

  const handleDeleteDesign = useCallback((id) => {
    setRecentDesigns(prev => prev.filter(d => d.id !== id));
  }, []);

  const handleDuplicateDesign = useCallback(async (design) => {
    // Persist the copy (POST /api/designs) so it opens correctly and
    // survives refresh; inherits the source thumbnail immediately.
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!API_URL || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${design.name || 'Untitled'} (copy)`,
          canvas_size: design.canvas_size || { width: 1080, height: 1080 },
          pages: design.pages || [],
          current_page_id: design.current_page_id || null,
          thumbnail: design.thumbnail || null,
          thumbnail_url: design.thumbnail_url || null,
        }),
      });
      if (!res.ok) return;
      const created = await res.json();
      setRecentDesigns(prev => [created, ...prev]);
    } catch { /* best-effort */ }
  }, []);

  const handleUnsaveTemplate = useCallback((id) => {
    setSavedTemplates(prev => prev.filter(t => t.id !== id));
  }, []);

  if (!user) return null;

  const firstName = user.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-6">

          {/* ── Welcome banner ── */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                {greeting()}, {firstName}! 👋
              </h1>
              <p className="text-gray-500 mt-1 text-sm">Here's your design workspace</p>
            </div>
            <button
              onClick={() => navigate('/editor')}
              className="flex items-center gap-2 h-10 px-5 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #ec4899)', boxShadow: '0 4px 14px rgba(124,58,237,0.35)' }}
            >
              <Plus className="w-4 h-4" /> New Design
            </button>
          </div>

          {/* ── Stats row ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatCard icon="🎨" value={recentDesigns.length} label="Designs" color="#7c3aed" />
            <StatCard icon="🔖" value={savedTemplates.length} label="Saved Templates" color="#f59e0b" />
            <StatCard icon="⚡" value={activity.length} label="Activities" color="#2563eb" />
          </div>

          {/* ── Recent Designs ── */}
          <Section
            title="Recent Designs"
            icon={ImagePlus}
            action={{ label: 'View all', onClick: () => navigate('/my-designs') }}
          >
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[0,1,2].map(i => <SkeletonCard key={i} />)}
              </div>
            ) : recentDesigns.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {recentDesigns.slice(0, 6).map(d => (
                  <DesignCard
                    key={d.id}
                    design={d}
                    onOpen={() => navigate(`/editor/${d.id}`)}
                    onDuplicate={() => handleDuplicateDesign(d)}
                    onDelete={() => handleDeleteDesign(d.id)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                emoji="🎨"
                title="No designs yet"
                desc="Start creating and your designs will appear here."
                actionLabel="+ Create your first design"
                onAction={() => navigate('/editor')}
              />
            )}
          </Section>

          {/* ── Bottom two columns ── */}
          <div className="grid md:grid-cols-5 gap-6">
            {/* Saved Templates */}
            <div className="md:col-span-3">
              <Section
                title="Saved Templates"
                icon={LayoutTemplate}
                action={{ label: 'Browse all', onClick: () => navigate('/templates') }}
              >
                {loading ? (
                  <div className="space-y-3">
                    {[0,1,2].map(i => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-gray-100 rounded w-3/4" />
                          <div className="h-2.5 bg-gray-100 rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : savedTemplates.length > 0 ? (
                  <div>
                    {savedTemplates.slice(0, 6).map(t => (
                      <TemplateRow
                        key={t.id}
                        template={t}
                        onOpen={() => navigate(`/editor?template=${t.id}`)}
                        onUnsave={() => handleUnsaveTemplate(t.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    emoji="🔖"
                    title="No saved templates"
                    desc="Browse templates and save the ones you love."
                    actionLabel="Browse Templates"
                    onAction={() => navigate('/templates')}
                  />
                )}
              </Section>
            </div>

            {/* Activity */}
            <div className="md:col-span-2">
              <Section title="Recent Activity" icon={Activity}>
                {activity.length > 0 ? (
                  <div>
                    {activity.slice(0, 10).map((item, i) => (
                      <ActivityItem key={i} item={item} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    emoji="⚡"
                    title="No activity yet"
                    desc="Your recent actions will appear here."
                  />
                )}
              </Section>
            </div>
          </div>

          {/* ── Quick actions ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { emoji: '📐', title: 'Browse Templates', desc: 'Find the perfect starting point', href: '/templates', color: '#7c3aed' },
              { emoji: '✏️', title: 'Open Editor', desc: 'Start with a blank canvas', href: '/editor', color: '#2563eb' },
              { emoji: '⚙️', title: 'Account Settings', desc: 'Update your profile', href: '/settings', color: '#059669' },
            ].map(({ emoji, title, desc, href }) => (
              <button
                key={href}
                onClick={() => navigate(href)}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
              >
                <div className="text-2xl mb-3">{emoji}</div>
                <div className="font-semibold text-sm text-gray-900 group-hover:text-violet-700 transition-colors">{title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
              </button>
            ))}
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UserDashboard;
